'use client';

import useMap from '@/hooks/useMap';
import useAnalytics from '@/hooks/useAnalytics';
import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { queryStringConstants } from '@/constants/queryConstants';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import Observer from '@/components/Observer';
import { getCertifiedViewFact } from '@/analytics/helpers/cventAnalytics/getCertifiedViewFact';
import { getAdClickFact } from '@/analytics/helpers/cventAnalytics/getAdClickFact';
import type { AdClickFact } from '@/analytics/models/cventAnalytics/AdClickFact';
import type { CertifiedViewExperimentData } from '@/analytics/models/cventAnalytics/CertifiedViewFact';
import { CSN_FACT_SOURCE_VENUE_CARD } from '@/utils/analyticsClientHelper';
import type { SearchParams } from '@/models/SearchParams';
import { getVenueUrl } from '@/utils/venueDetailsHelper';
import type { Locale } from '@/i18n';
import useSpinner from '@/hooks/useSpinner';

export type Props = {
  venueIndex: number;
  venue: CsnSearchVenue;
  searchTerm: string;
  children: JSX.Element;
  certifiedViewExperimentData: CertifiedViewExperimentData;
  environment: string;
  className?: string;
  searchResultsRank?: number;
  locale: Locale;
  searchParams: SearchParams;
  cventGuestDomain: string;
  basePath: string;
  isMicrosite: boolean;
};

export default function AdsContainer({
  venueIndex,
  venue,
  searchTerm,
  certifiedViewExperimentData,
  environment,
  searchResultsRank,
  locale,
  searchParams,
  cventGuestDomain,
  basePath,
  isMicrosite,
  children,
  className
}: Props): JSX.Element {
  const { mapExpanded } = useMap();
  const analytics = useAnalytics();
  const params = useSearchParams();
  const { setShowSpinner } = useSpinner();

  const handleClick = useCallback(
    async e => {
      e.preventDefault();
      setShowSpinner(true);
      const venueProfileFactParams = {
        adId: venue.listingCode,
        adType: venue.featuredLevel,
        adPos: venue.listingCode ? venueIndex + 1 : undefined,
        searchResultsRank,
        searchTerm
      };
      const adClickFact: AdClickFact = getAdClickFact(
        venue,
        venueIndex,
        mapExpanded,
        searchTerm,
        params?.get(queryStringConstants.RFP_ID),
        CSN_FACT_SOURCE_VENUE_CARD
      );
      await analytics.reportFactImmediately<AdClickFact>(adClickFact);

      const venueUrl = !isMicrosite
        ? getVenueUrl(venue, locale, cventGuestDomain, basePath, searchParams, venueProfileFactParams)
        : getVenueUrl(venue, locale, cventGuestDomain, basePath);
      // handles command + click event
      if (e.metaKey) {
        setShowSpinner(false);
        window.open(venueUrl);
      } else {
        window.open(venueUrl, '_self');
      }
    },
    [
      venue,
      venueIndex,
      mapExpanded,
      searchTerm,
      params,
      searchParams,
      analytics,
      searchResultsRank,
      locale,
      cventGuestDomain,
      basePath,
      isMicrosite,
      setShowSpinner
    ]
  );

  const observerPayload = useMemo(
    () => getCertifiedViewFact(searchTerm, mapExpanded, venueIndex, venue, environment),
    [environment, mapExpanded, searchTerm, venue, venueIndex]
  );

  return (
    /* don't want wrapper div to be accessible to screen reader */
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
    <div className={className} onClick={handleClick}>
      <Observer payload={observerPayload} certifiedViewExperimentData={certifiedViewExperimentData}>
        {children}
      </Observer>
    </div>
  );
}
